export const EVENT_TIMEZONE = 'Europe/Helsinki';
export const EVENT_START_TIME = '17:00:00';

export type RemainingTime = {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    totalMs: number;
};

const pad = (value: number): string => String(value).padStart(2, '0');

const partNumber = (parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): number => {
    const value = parts.find(part => part.type === type)?.value ?? '0';
    const parsed = Number(value);
    return type === 'hour' && parsed === 24 ? 0 : parsed;
};

/**
 * Offset of `timeZone` relative to UTC at `date`, in milliseconds.
 * Positive values are east of UTC (e.g. Europe/Helsinki in winter is +7200000).
 */
export const getTimeZoneOffsetMs = (date: Date, timeZone: string): number => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    }).formatToParts(date);

    const asUtc = Date.UTC(
        partNumber(parts, 'year'),
        partNumber(parts, 'month') - 1,
        partNumber(parts, 'day'),
        partNumber(parts, 'hour'),
        partNumber(parts, 'minute'),
        partNumber(parts, 'second')
    );

    return asUtc - date.getTime();
};

/**
 * Converts a calendar date + clock time in a named timezone to a UTC instant.
 * `date` is YYYY-MM-DD, `time` is HH:mm:ss.
 */
export const zonedDateTimeToUtc = (date: string, time: string, timeZone: string): Date => {
    const utcGuess = new Date(`${date}T${time}Z`);
    if (Number.isNaN(utcGuess.getTime())) {
        return utcGuess;
    }

    const firstPass = new Date(utcGuess.getTime() - getTimeZoneOffsetMs(utcGuess, timeZone));
    const correctedOffset = getTimeZoneOffsetMs(firstPass, timeZone);
    return new Date(utcGuess.getTime() - correctedOffset);
};

export const getEventStartDate = (
    startDate: string,
    time: string = EVENT_START_TIME,
    timeZone: string = EVENT_TIMEZONE
): Date => zonedDateTimeToUtc(startDate, time, timeZone);

export const getRemainingTime = (target: Date, now: Date = new Date()): RemainingTime => {
    const totalMs = Math.max(0, target.getTime() - now.getTime());
    const totalSeconds = Math.floor(totalMs / 1000);

    return {
        days: Math.floor(totalSeconds / 86400),
        hours: Math.floor((totalSeconds % 86400) / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60,
        totalMs
    };
};

export const formatRemainingTimeLabel = ({ days, hours, minutes, seconds }: RemainingTime): string =>
    `${days} päivää, ${hours} tuntia, ${minutes} minuuttia, ${seconds} sekuntia`;

export const formatUnitValue = (value: number, minDigits = 2): string =>
    minDigits === 2 ? pad(value) : String(value);
